/** Static rrweb backdrop: renders a recorded FullSnapshot as the page image the
 *  heatmap paints over, so the overlay sits on the REAL page instead of the
 *  hardcoded wireframe.
 *
 *  Why this works: we store rrweb FullSnapshots for every recorded session, so
 *  the most recent one is a faithful, same-origin render of the customer's page
 *  at a known viewport. Constructing rrweb's Replayer WITHOUT calling play()
 *  leaves it parked on the initial frame — exactly a screenshot, without needing
 *  a headless-browser screenshot service.
 *
 *  Coordinate contract: coarse (heatmap) points are normalized 0..1 against the
 *  recorded viewport, so the host sizes its box to the recorded aspect ratio and
 *  this component simply scales the iframe to fill it. Points then line up.
 */
import { useEffect, useRef } from "react";
import { Replayer } from "rrweb";
import type { eventWithTime } from "rrweb";
import "rrweb/dist/style.css";

export function PageSnapshotBackdrop({
  events,
  viewport,
}: {
  events: unknown[];
  viewport: { width: number; height: number };
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const replayerRef = useRef<Replayer | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || events.length < 2) return;
    host.innerHTML = "";

    let replayer: Replayer | null = null;
    try {
      replayer = new Replayer(events as eventWithTime[], {
        root: host,
        liveMode: false,
        showWarning: false,
        blockClass: "rr-block",
        // Never autoplay: the first frame IS the backdrop.
        speed: 1,
        skipInactive: false,
      });
      replayerRef.current = replayer;

      // Scale the fixed-size recorded iframe down to the host box.
      const fit = () => {
        const iframe = host.querySelector("iframe") as HTMLIFrameElement | null;
        if (!iframe) return;
        const w = host.clientWidth || 1;
        const scale = w / (viewport.width || 1024);
        iframe.style.transform = `scale(${scale})`;
        iframe.style.transformOrigin = "top left";
        iframe.style.width = `${viewport.width}px`;
        iframe.style.height = `${viewport.height}px`;
        iframe.style.border = "0";
        iframe.style.pointerEvents = "none";
      };
      fit();
      const ro = new ResizeObserver(fit);
      ro.observe(host);
      return () => {
        ro.disconnect();
        try {
          replayer?.destroy();
        } catch {
          /* ignore teardown races */
        }
        replayerRef.current = null;
      };
    } catch {
      // Unsupported recording: the host keeps its wireframe fallback.
      try {
        replayer?.destroy();
      } catch {
        /* ignore */
      }
      return;
    }
  }, [events, viewport.width, viewport.height]);

  return <div ref={hostRef} className="absolute inset-0 overflow-hidden bg-white" aria-hidden />;
}
